/**
 * Sticky Add to Cart bar.
 *
 * Design principle (per project instruction): this component NEVER maintains
 * its own purchase-mode (one-time vs. subscription) state and NEVER builds
 * its own cart/add request. It only mirrors the real product form's state
 * for display, and at click time it re-reads the real form to decide whether
 * it is safe to click the real, existing Add to Cart button on the
 * shopper's behalf. It never writes to the real form's `selling_plan` input.
 *
 * Relies only on the theme's existing globals (subscribe/publish/PUB_SUB_EVENTS
 * from constants.js + pubsub.js, both loaded globally in theme.liquid) and on
 * IDs/classes that already exist in this theme's main-product section and
 * buy-buttons/quantity snippets. Nothing here alters those files.
 */
(function () {
  if (customElements.get('sticky-add-to-cart')) return;

  class StickyAddToCart extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.formId = this.dataset.productFormId;
      this.observeTargetId = this.dataset.observeTarget;

      this.realForm = document.getElementById(this.formId);
      this.realSubmitButton = document.getElementById(`ProductSubmitButton-${this.sectionId}`);
      this.observeTarget = document.getElementById(this.observeTargetId) || this.realForm;

      this.submitBtn = this.querySelector(`#StickyAtcSubmit-${this.sectionId}`);
      this.submitLabel = this.querySelector(`#StickyAtcSubmitLabel-${this.sectionId}`);
      this.priceEl = this.querySelector(`#StickyAtcPrice-${this.sectionId}`);
      this.variantEl = this.querySelector(`#StickyAtcVariant-${this.sectionId}`);
      this.noticeEl = this.querySelector(`#StickyAtcNotice-${this.sectionId}`);
      this.qtyWrap = this.querySelector(`#StickyAtcQtyWrap-${this.sectionId}`);
      this.qtyInput = this.querySelector(`#StickyAtcQty-${this.sectionId}`);
      this.qtyMinus = this.querySelector('[data-sticky-qty-minus]');
      this.qtyPlus = this.querySelector('[data-sticky-qty-plus]');
      this.priceSourceEl = document.getElementById(`price-${this.sectionId}`);

      this.currentVariant = this.readInitialVariant();

      // Reveal (CSS-controlled) now that JS has taken over from the
      // server-rendered `hidden` guard against a flash of the bar before
      // the IntersectionObserver has had a chance to run.
      this.hidden = false;

      this.setupObserver();
      this.setupVariantSync();
      this.setupSubmitButtonMirror();
      this.setupSellingPlanSync();
      this.setupQuantity();
      this.setupSubmit();
    }

    disconnectedCallback() {
      this.io?.disconnect();
      this.buttonObserver?.disconnect();
      this.realQtyObserver?.disconnect();
      this.sellingPlanObserver?.disconnect();
      this.unsubscribeVariantChange?.();
    }

    readInitialVariant() {
      const script = this.querySelector('script[data-sticky-atc-initial-variant]');
      if (!script) return null;
      try {
        const parsed = JSON.parse(script.textContent);
        return parsed && parsed.id ? parsed : null;
      } catch (e) {
        return null;
      }
    }

    // --- Show/hide on scroll -------------------------------------------------

    setupObserver() {
      if (!this.observeTarget || !('IntersectionObserver' in window)) return;

      this.io = new IntersectionObserver(
        (entries) => {
          const entry = entries[entries.length - 1];
          // Show once the real buy box has been scrolled past (above the
          // viewport). Stays hidden while it's visible, and while it hasn't
          // been reached yet (still below the viewport).
          const scrolledPast = !entry.isIntersecting && entry.boundingClientRect.top < 0;
          this.toggleVisibility(scrolledPast);
        },
        { threshold: 0 }
      );
      this.io.observe(this.observeTarget);
    }

    toggleVisibility(show) {
      this.classList.toggle('sticky-atc--visible', show);
    }

    // --- Variant / price sync -------------------------------------------------

    setupVariantSync() {
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribeVariantChange = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          if (!event || !event.data || String(event.data.sectionId) !== String(this.sectionId)) return;
          this.applyVariant(event.data.variant);
        });
      }
      this.applyVariant(this.currentVariant);
    }

    applyVariant(variant) {
      this.currentVariant = variant || null;

      if (!variant) {
        // No variant matches the currently selected options (Dawn's own
        // "unavailable combination" state) — never purchasable.
        if (this.submitBtn) this.submitBtn.disabled = true;
        return;
      }

      if (this.variantEl) {
        this.variantEl.textContent = variant.public_title || variant.title || '';
      }

      this.updatePriceDisplay();
    }

    updatePriceDisplay() {
      if (!this.priceEl) return;

      const activePlan = this.getActiveSellingPlanId();
      const allocations = (this.currentVariant && this.currentVariant.selling_plan_allocations) || [];
      const validAllocation = activePlan
        ? allocations.find((a) => String(a.selling_plan_id) === String(activePlan))
        : null;

      if (validAllocation && typeof validAllocation.price === 'number') {
        this.priceEl.textContent = this.formatMoney(validAllocation.price);
        return;
      }

      // Mirror the real one-time price block. Dawn keeps `#price-{{ section.id }}`
      // updated on every variant change even when it's visually hidden (which it
      // is on every product that offers Seal subscriptions in this theme), so its
      // text is a reliable, already-localized source — no formatting is invented
      // here for the one-time case.
      const oneTimeText = this.readOneTimePriceText();
      if (oneTimeText) {
        this.priceEl.textContent = oneTimeText;
        return;
      }

      if (this.currentVariant && typeof this.currentVariant.price === 'number') {
        this.priceEl.textContent = this.formatMoney(this.currentVariant.price);
      }
    }

    // Reads only the single currently-active price figure out of the real
    // `#price-{{ section.id }}` block, rather than that whole block's
    // textContent. Confirmed against this theme's own snippets/price.liquid:
    // Dawn always renders BOTH `.price__regular` and `.price__sale` (plus the
    // "Sale"/"Sold out" badge spans when show_badges is on) in the DOM at the
    // same time and relies on CSS — keyed off a `price--on-sale` /
    // `price--sold-out` modifier class on the outer `.price` element — to
    // show only the applicable one. Because the whole block is also always
    // CSS-hidden here (every MWN retail product uses Seal subscriptions, so
    // `#price-{{ section.id }}` always carries the `hidden` class),
    // getComputedStyle()/offsetParent can't be used to tell which sub-block
    // "would" be visible — display:none on the hidden ancestor makes every
    // descendant compute to display:none too. The `price--on-sale` class
    // itself is unaffected by that and is exactly the same condition
    // (`compare_at_price > price`) snippets/price.liquid uses to pick the
    // block, so it's used directly instead. Badge spans are never read here;
    // sold-out state is mirrored separately via the real submit button.
    readOneTimePriceText() {
      const source = this.priceSourceEl || document.getElementById(`price-${this.sectionId}`);
      const priceEl = source && source.querySelector('.price');
      if (!priceEl) return '';

      const onSale = priceEl.classList.contains('price--on-sale');
      const activeRow = priceEl.querySelector(onSale ? '.price__sale' : '.price__regular');
      const priceItem = activeRow && activeRow.querySelector(onSale ? '.price-item--sale' : '.price-item--regular');
      if (!priceItem) return '';

      return priceItem.textContent.replace(/\s+/g, ' ').trim();
    }

    formatMoney(cents) {
      // Subscription-discounted prices aren't present in the server-rendered
      // one-time price block, so this is computed from real Shopify data
      // (the variant's own selling_plan_allocations) and formatted using the
      // currency symbol lifted from the store's own rendered one-time price —
      // both AU and US render "$X.XX" today (confirmed via live QA on both
      // stores), so this adapts to that symbol rather than hard-coding it.
      //
      // The symbol is lifted from readOneTimePriceText()'s already-isolated
      // price string (e.g. "A$37.99"), not the raw price block textContent —
      // that raw text also contains Dawn's visually-hidden "Regular price" /
      // "Sale price" screen-reader labels, and a naive leading-character
      // match against it grabs "Regular" instead of the currency symbol.
      const sample = this.readOneTimePriceText() || '';
      const symbolMatch = sample.match(/^[^\d\s]+/);
      const symbol = symbolMatch ? symbolMatch[0] : '$';
      return `${symbol}${(cents / 100).toFixed(2)}`;
    }

    getSellingPlanInput() {
      return this.realForm ? this.realForm.querySelector('input[name="selling_plan"]') : null;
    }

    getActiveSellingPlanId() {
      const input = this.getSellingPlanInput();
      return input && input.value ? input.value : null;
    }

    // --- Selling-plan (subscription) sync --------------------------------------

    // The purchase-option control (One-time purchase / Subscribe & Save) on
    // this theme is rendered by the Seal Subscriptions app, not Dawn's native
    // selling-plan selector. Confirmed live: switching it writes the new plan
    // straight into the real `selling_plan` input's `value` ATTRIBUTE via
    // script, without dispatching any `change`/`input` event on it or on the
    // radios — so a MutationObserver on that attribute (the same pattern
    // already used above for quantity sync) is the only reliable signal that
    // the shopper's purchase option changed, and is what keeps the sticky
    // bar's price in sync with it.
    //
    // Observing the real FORM (subtree), not the `selling_plan` input
    // directly: confirmed live that Seal's own script inserts/populates that
    // input slightly after this component's connectedCallback runs, so it
    // isn't reliably present yet at setup time. Watching the form's subtree
    // catches both its eventual insertion and every later value change,
    // whichever order those happen in. updatePriceDisplay() is cheap and
    // idempotent, so firing it on other incidental form mutations too is
    // harmless.
    setupSellingPlanSync() {
      if (!this.realForm) return;

      this.sellingPlanObserver = new MutationObserver(() => this.updatePriceDisplay());
      this.sellingPlanObserver.observe(this.realForm, {
        subtree: true,
        childList: true,
        attributes: true,
        attributeFilter: ['value'],
      });
    }

    // --- Sold-out / unavailable mirroring --------------------------------------

    setupSubmitButtonMirror() {
      if (!this.realSubmitButton) return;
      this.syncFromRealButton();
      this.buttonObserver = new MutationObserver(() => this.syncFromRealButton());
      this.buttonObserver.observe(this.realSubmitButton, {
        attributes: true,
        attributeFilter: ['disabled'],
        childList: true,
        subtree: true,
        characterData: true,
      });
    }

    syncFromRealButton() {
      if (!this.realSubmitButton || !this.submitBtn) return;
      const disabled = this.realSubmitButton.disabled;
      this.submitBtn.disabled = disabled;
      this.submitBtn.classList.toggle('sticky-atc__submit--disabled', disabled);

      const realLabelEl = this.realSubmitButton.querySelector('span');
      const realLabel = realLabelEl ? realLabelEl.textContent.trim() : '';
      if (this.submitLabel && realLabel) this.submitLabel.textContent = realLabel;

      // The real button's disabled/text state changes on variant swaps too,
      // so this is a convenient second trigger to keep price in sync.
      this.updatePriceDisplay();
    }

    // --- Quantity sync (only if a quantity input is exposed on this PDP) ------

    setupQuantity() {
      this.realQtyInput = document.getElementById(`Quantity-${this.sectionId}`);
      if (!this.realQtyInput) {
        if (this.qtyWrap) this.qtyWrap.hidden = true;
        return;
      }

      if (this.qtyWrap) this.qtyWrap.hidden = false;
      this.qtyInput.value = this.realQtyInput.value || '1';
      if (this.realQtyInput.min) this.qtyInput.min = this.realQtyInput.min;
      if (this.realQtyInput.max) this.qtyInput.max = this.realQtyInput.max;

      this.realQtyObserver = new MutationObserver(() => this.syncQtyFromReal());
      this.realQtyObserver.observe(this.realQtyInput, {
        attributes: true,
        attributeFilter: ['value', 'min', 'max'],
      });
      this.realQtyInput.addEventListener('change', () => this.syncQtyFromReal());

      this.qtyInput.addEventListener('change', () => this.syncQtyToReal());
      this.qtyMinus?.addEventListener('click', () => this.stepQty(-1));
      this.qtyPlus?.addEventListener('click', () => this.stepQty(1));
    }

    syncQtyFromReal() {
      if (!this.realQtyInput || !this.qtyInput) return;
      if (document.activeElement === this.qtyInput) return; // don't fight an in-progress edit
      this.qtyInput.value = this.realQtyInput.value;
      if (this.realQtyInput.min) this.qtyInput.min = this.realQtyInput.min;
      if (this.realQtyInput.max) this.qtyInput.max = this.realQtyInput.max;
    }

    syncQtyToReal() {
      if (!this.realQtyInput || !this.qtyInput) return;
      let value = parseInt(this.qtyInput.value, 10);
      const min = parseInt(this.qtyInput.min, 10) || 1;
      if (isNaN(value) || value < min) value = min;
      this.qtyInput.value = value;
      // The real input carries `form="{{ product_form_id }}"`, so setting its
      // value here is what actually reaches Shopify on submit — this is not
      // decorative.
      this.realQtyInput.value = value;
      this.realQtyInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    stepQty(delta) {
      if (!this.qtyInput) return;
      let value = parseInt(this.qtyInput.value, 10) || 1;
      const min = parseInt(this.qtyInput.min, 10) || 1;
      value = Math.max(min, value + delta);
      this.qtyInput.value = value;
      this.syncQtyToReal();
    }

    // --- Submit: always delegates to the real form/button ----------------------

    setupSubmit() {
      if (!this.submitBtn) return;
      this.submitBtn.addEventListener('click', (event) => {
        event.preventDefault();
        this.handleStickySubmit();
      });
    }

    handleStickySubmit() {
      if (!this.realForm || !this.realSubmitButton) return;
      if (this.realSubmitButton.disabled) return; // mirrors real sold-out/unavailable state

      const variantIdInput = this.realForm.querySelector('input[name="id"]');
      const currentVariantId = variantIdInput ? variantIdInput.value : null;
      const activePlan = this.getActiveSellingPlanId();

      if (activePlan) {
        // Steps 1–3 from spec: read the selected variant and the live
        // selling_plan value from the real form, then verify the plan is
        // still valid for that exact variant before allowing submission.
        const variantMatchesTrackedState =
          !this.currentVariant || String(this.currentVariant.id) === String(currentVariantId);
        const allocations = (this.currentVariant && this.currentVariant.selling_plan_allocations) || [];
        const planStillValid =
          variantMatchesTrackedState && allocations.some((a) => String(a.selling_plan_id) === String(activePlan));

        if (!currentVariantId || !planStillValid) {
          // Step 5: don't submit a stale plan, and don't silently switch
          // purchase type ourselves — hand the shopper back to the real
          // purchase area so they can confirm what they want.
          this.surfaceToMainPurchaseArea();
          return;
        }
      }

      // Step 4: valid (or a plain one-time purchase) — click the real button
      // so Shopify receives exactly what a normal click would have sent.
      this.realSubmitButton.click();
    }

    surfaceToMainPurchaseArea() {
      if (this.noticeEl) {
        this.noticeEl.textContent =
          'Please reselect your subscription option above before adding to cart.';
        this.noticeEl.hidden = false;
        window.clearTimeout(this._noticeTimeout);
        this._noticeTimeout = window.setTimeout(() => {
          this.noticeEl.hidden = true;
        }, 6000);
      }
      const target = this.observeTarget || this.realForm;
      target?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }

  customElements.define('sticky-add-to-cart', StickyAddToCart);
})();
